import { INACTIVE_READ_ONLY_STATUS, type ReadOnlyStatus } from '@oci/shared';
import { useSyncExternalStore } from 'react';
import { getFormatLocale } from './i18n/format';
import { translate } from './i18n/state';
import { TIME_OF_DAY } from './utils';

/**
 * Read-only maintenance mode in the browser (v0.11 design, section 9).
 *
 * One small store, outside React Query so the composer and the message
 * actions can read it without a query client: the banner keeps it current by
 * polling `/api/maintenance`, and any write the API refuses with `423
 * READ_ONLY` (a write that raced the switch) updates it at once from the
 * refusal itself, so the page explains why instead of showing an error.
 */

let current: ReadOnlyStatus = INACTIVE_READ_ONLY_STATUS;
const listeners = new Set<() => void>();

export function readOnlyStatus(): ReadOnlyStatus {
  return current;
}

export function setReadOnlyStatus(next: ReadOnlyStatus): void {
  if (
    next.active === current.active &&
    next.source === current.source &&
    next.reason === current.reason &&
    next.until === current.until &&
    next.window?.startsAt === current.window?.startsAt &&
    next.window?.endsAt === current.window?.endsAt
  )
    return;
  current = next;
  for (const listener of listeners) listener();
}

/** Calls `listener` whenever the status changes; returns the unsubscribe. */
export function subscribeReadOnlyStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current status; re-renders when it changes. */
export function useReadOnlyStatus(): ReadOnlyStatus {
  return useSyncExternalStore(subscribeReadOnlyStatus, readOnlyStatus, readOnlyStatus);
}

/** Whether a value has the shape of `GET /api/maintenance`'s answer. */
export function isReadOnlyStatus(value: unknown): value is ReadOnlyStatus {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ReadOnlyStatus).active === 'boolean'
  );
}

/**
 * Notes a refusal's body (`{ error: { code: 'READ_ONLY', details: { readOnly } } }`).
 * Returns whether it was one.
 */
export function noteReadOnlyRefusal(body: unknown): boolean {
  const error = (body as { error?: { code?: unknown; details?: { readOnly?: unknown } } } | null)
    ?.error;
  if (error?.code !== 'READ_ONLY') return false;
  const status = error.details?.readOnly;
  setReadOnlyStatus(
    isReadOnlyStatus(status)
      ? status
      : { ...INACTIVE_READ_ONLY_STATUS, active: true, source: 'administrator' },
  );
  return true;
}

/**
 * The read-only status a Better Auth client error carries, when it is a
 * read-only refusal: the client spreads the API's body into the error
 * (`{ error: { code: 'READ_ONLY', … }, status: 423 }`). Notes it as well, so
 * the banner and the other controls follow. Null for any other error.
 */
export function authReadOnlyRefusal(error: unknown): ReadOnlyStatus | null {
  if ((error as { status?: unknown } | null)?.status !== 423) return null;
  return noteReadOnlyRefusal(error) ? current : null;
}

/** Reads a `423` response's body without consuming the caller's copy. */
export async function noteReadOnlyResponse(response: Response): Promise<void> {
  if (response.status !== 423) return;
  try {
    noteReadOnlyRefusal(await response.clone().json());
  } catch {
    // Not a JSON body: not ours.
  }
}

/**
 * "14:30 BST" today, "Mon 5 Oct, 14:30 BST" on another day this year, and with
 * the year in another year ("Sun 10 Jan 2027, 14:30 GMT", #81), in the
 * person's own zone, named: a scheduled window's announcement gives its times
 * in the instance's zone, and an unnamed local time beside it read as a
 * different time (#160). The time of day is written as every admin list
 * writes it (`formatDateTime`, "6:30 AM"): a two-digit hour gave "07:30 AM"
 * here beside "6:30 AM" there (#305).
 */
export function formatReadOnlyTime(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const sameDay = date.toDateString() === now.toDateString();
  const sameYear = date.getFullYear() === now.getFullYear();
  // The language the app is showing, not the browser's: the formatter's styles
  // have no weekday, day and zone name together, so it is built here.
  return date.toLocaleString(getFormatLocale(), {
    ...(sameDay ? {} : { weekday: 'short', day: 'numeric', month: 'short' }),
    ...(sameYear ? {} : { year: 'numeric' }),
    ...TIME_OF_DAY,
    timeZoneName: 'short',
  });
}

/** How every refusal's text (`readOnlyMessage`) begins, in English. */
export const READ_ONLY_MESSAGE_START = 'Read-only for maintenance';

/**
 * How every refusal's text begins in the language being shown: whoever wrote
 * the refusal may have been showing another, so callers also test the English.
 */
export function readOnlyMessageStarts(): string[] {
  return [READ_ONLY_MESSAGE_START, translate('shell.readOnly.short')];
}

/** The administrator's reason as a sentence of its own, with the space that joins it to what comes before. */
function reasonSentence(status: ReadOnlyStatus): string {
  return status.reason ? ` ${status.reason.trim().replace(/([^.!?])$/, '$1.')}` : '';
}

/** One sentence for people: why changes are paused, and until when if known. */
export function readOnlyMessage(status: ReadOnlyStatus, now = new Date()): string {
  const reason = reasonSentence(status);
  return status.until
    ? translate('shell.readOnly.messageUntil', {
        time: formatReadOnlyTime(status.until, now),
        reason,
      })
    : translate('shell.readOnly.message', { reason });
}

/**
 * Forgot password while read-only: a reset is a change, so it is refused and
 * no email goes out; say so rather than "a link has been sent" (#138).
 */
export function passwordResetPausedMessage(status: ReadOnlyStatus, now = new Date()): string {
  const reason = reasonSentence(status);
  return status.until
    ? translate('shell.readOnly.passwordResetUntil', {
        time: formatReadOnlyTime(status.until, now),
        reason,
      })
    : translate('shell.readOnly.passwordReset', { reason });
}

/** Short reason for a disabled control's tooltip. */
export function readOnlyShortReason(status: ReadOnlyStatus, now = new Date()): string {
  return status.until
    ? translate('shell.readOnly.shortUntil', { time: formatReadOnlyTime(status.until, now) })
    : translate('shell.readOnly.short');
}

/**
 * For a control that changes something: whether it is off now, and the
 * reason for its title, as the message and sidebar actions show it (#159).
 * The conversation header's Rename, Share and Move to project, and the
 * sidebar's New project, stayed on while the sidebar's own Rename was off,
 * and only refused once used (#331).
 */
export function useReadOnlyLock(): { locked: boolean; title: string | undefined } {
  const status = useReadOnlyStatus();
  return { locked: status.active, title: status.active ? readOnlyShortReason(status) : undefined };
}
